// Generated by sdk/build-browser.js. Configure the backend URL explicitly.
/**
 * @life-suite/sdk
 *
 * Lightweight client for connecting Life Canvas, Life Pulse, and Life Connect
 * to the RAD Consulting Group Virtual Organization backend.
 *
 * Usage:
 *   import { LifeSuiteClient } from '@life-suite/sdk';
 *
 *   const client = new LifeSuiteClient({
 *     environment: 'prod',
 *     instanceUrls: {
 *       dev: 'http://localhost:3100',
 *       test: 'https://test-life-suite.example.com',
 *       prod: 'https://life-suite.example.com',
 *     },
 *     apiKey: 'your-app-api-key',
 *     appTag: 'life_canvas',  // or 'life_pulse', 'life_connect'
 *   });
 *
 *   // Widget — ask a question
 *   const answer = await client.ask('How do I create a goal?', { screen: 'dashboard' });
 *
 *   // Support — submit a ticket
 *   const ticket = await client.submitTicket({ content: 'Budget not syncing', category: 'sync' });
 *
 *   // Issues — record a bug or enhancement
 *   const issue = await client.createIssue({ title: 'Welcome dialog progress missing', description: 'Education progress is not shown.', record_type: 'enhancement' });
 *
 *   // Support — check status
 *   const status = await client.ticketStatus(ticket.ticket_id);
 *
 *   // Events — report an error
 *   await client.sendEvent('unhandled_error', { stack: '...' }, 'critical');
 *
 *   // KB — search knowledge base
 *   const results = await client.searchKB('glidepath', { category: 'goals' });
 *
 *   // Feedback — user rates an answer
 *   await client.feedback(sessionId, 'positive');
 */

const DEFAULT_INSTANCE_URLS = {
  dev: "http://localhost:3100",
  test: "",
  prod: "",
};

function resolveEnvironment(environment) {
  return String(
    environment
    || readEnvValue("NEXT_PUBLIC_LIFE_SUITE_ENV")
    || readEnvValue("LIFE_SUITE_ENV")
    || "dev"
  ).toLowerCase();
}

function readEnvValue() { return ""; }

function resolveInstanceUrls(instanceUrls = {}) {
  return {
    dev: instanceUrls.dev || readEnvValue("NEXT_PUBLIC_LIFE_SUITE_DEV_URL") || readEnvValue("LIFE_SUITE_DEV_URL") || DEFAULT_INSTANCE_URLS.dev,
    test: instanceUrls.test || readEnvValue("NEXT_PUBLIC_LIFE_SUITE_TEST_URL") || readEnvValue("LIFE_SUITE_TEST_URL") || DEFAULT_INSTANCE_URLS.test,
    prod: instanceUrls.prod || readEnvValue("NEXT_PUBLIC_LIFE_SUITE_PROD_URL") || readEnvValue("LIFE_SUITE_PROD_URL") || DEFAULT_INSTANCE_URLS.prod,
  };
}

function resolveBaseUrl(baseUrl, environment = "dev", instanceUrls = {}) {
  if (baseUrl) return baseUrl.replace(/\/$/, "");

  const normalizedEnvironment = resolveEnvironment(environment);
  const urls = resolveInstanceUrls(instanceUrls);
  const resolved = urls[normalizedEnvironment];

  if (!resolved) {
    throw new Error(`No baseUrl configured for environment '${normalizedEnvironment}'. Provide baseUrl or instanceUrls.${normalizedEnvironment}.`);
  }

  return resolved.replace(/\/$/, "");
}

class LifeSuiteClient {
  constructor({ baseUrl, environment = "dev", instanceUrls, apiKey, appTag, sessionToken, getSessionToken, timeout = 15000 } = {}) {
    if (!appTag) throw new Error("appTag is required (life_canvas | life_pulse | life_connect | life_circle)");
    if (!apiKey && !sessionToken && !getSessionToken) throw new Error("A signed customer session or server API key is required.");

    this.environment = resolveEnvironment(environment);
    this.instanceUrls = resolveInstanceUrls(instanceUrls);
    this.baseUrl = resolveBaseUrl(baseUrl, this.environment, this.instanceUrls);
    this.apiKey = apiKey;
    this.sessionToken = sessionToken;
    this.getSessionToken = getSessionToken;
    this.appTag = appTag;
    this.timeout = timeout;
  }

  // ─── Internal fetch wrapper ────────────────────────────────────────────

  async _request(method, path, body = null) {
    const url = `${this.baseUrl}/api${path}`;
    const headers = {
      "Content-Type": "application/json",
    };
    if (this.apiKey) headers["x-api-key"] = this.apiKey;
    const token = this.getSessionToken ? await this.getSessionToken() : this.sessionToken;
    if (token) headers.Authorization = `Bearer ${token}`;

    const opts = {
      method,
      headers,
      signal: AbortSignal.timeout(this.timeout),
    };
    if (body) opts.body = JSON.stringify(body);

    const resp = await fetch(url, opts);
    const data = await resp.json();

    if (!resp.ok) {
      const err = new Error(data.message || data.error || `HTTP ${resp.status}`);
      err.status = resp.status;
      err.data = data;
      throw err;
    }

    return data;
  }

  // ─── Widget ────────────────────────────────────────────────────────────

  /**
   * Ask a question through the support widget.
   * @param {string} question - The user's question
   * @param {object} context - Optional context { screen, version, session_id }
   * @returns {Promise<{ status, answer?, message?, ticket_id? }>}
   */
  async ask(question, context = {}) {
    return this._request("POST", "/widget/ask", {
      question,
      ...context,
    });
  }

  /**
   * Get widget configuration for the app (suggested prompts, placeholder, etc.)
   * @returns {Promise<object>}
   */
  async widgetConfig() {
    return this._request("GET", "/widget/config");
  }

  createConversation(context = {}) { return this._request("POST", "/conversations", context); }
  article(id, revision, version = "") { return this._request("GET", `/kb/articles/${encodeURIComponent(id)}?revision=${encodeURIComponent(revision)}&version=${encodeURIComponent(version)}`); }
  listConversations(before) { return this._request("GET", `/conversations${before ? `?before=${encodeURIComponent(before)}` : ""}`); }
  conversation(id, before) { return this._request("GET", `/conversations/${encodeURIComponent(id)}${before ? `?before=${encodeURIComponent(before)}` : ""}`); }
  sendMessage(id, text, requestId, context = {}) {
    return this._request("POST", `/conversations/${encodeURIComponent(id)}/messages`, { text, request_id: requestId, ...context });
  }

  // ─── Support ───────────────────────────────────────────────────────────

  /**
   * Submit a support ticket.
   * @param {object} ticket - { content, category?, user_id?, screen?, version?, context? }
   * @returns {Promise<{ status, ticket_id, priority }>}
   */
  async submitTicket(ticket) {
    return this._request("POST", "/support/ticket", ticket);
  }

  /**
   * Check ticket status.
   * @param {string} ticketId
   * @returns {Promise<{ ticket_id, status, priority, content, response, user_updates, approver_responses, resolution_note, created_at, resolved_at }>}
   */
  async ticketStatus(ticketId) {
    return this._request("GET", `/support/status/${encodeURIComponent(ticketId)}`);
  }

  /**
   * List tickets for a user.
   * @param {string} userId
   * @param {number} [limit=20]
   * @returns {Promise<{ count, tickets[] }>}
   */
  async listTickets(userId, limit = 20) {
    const params = new URLSearchParams();
    if (userId) params.set("user_id", userId);
    if (limit) params.set("limit", String(limit));
    return this._request("GET", `/support/tickets?${params}`);
  }

  /**
   * Append a user note to an existing ticket.
   * @param {string} ticketId
   * @param {string} text
   * @returns {Promise<{ ok, update }>}
   */
  async updateTicket(ticketId, text) {
    return this._request("POST", `/support/${encodeURIComponent(ticketId)}/update`, { text });
  }

  /**
   * Cancel a pending or reopened ticket.
   * @param {string} ticketId
   * @returns {Promise<{ ok }>}
   */
  async cancelTicket(ticketId) {
    return this._request("POST", `/support/${encodeURIComponent(ticketId)}/cancel`, {});
  }

  /**
   * Reopen a cancelled or rejected ticket.
   * @param {string} ticketId
   * @param {string} [reason]
   * @returns {Promise<{ ok, status, update }>}
   */
  async reopenTicket(ticketId, reason) {
    return this._request("POST", `/support/${encodeURIComponent(ticketId)}/reopen`, { reason });
  }

  // ─── Issues ────────────────────────────────────────────────────────────

  /**
   * Create a product issue record.
  * @param {object} issue - { title?, summary?, description?, content?, record_type?, category?, labels?, acceptance_criteria?, source_channel?, queue_for_approval?, user_id?, session_id?, screen?, version?, metadata? }
   * @returns {Promise<{ status, issue_id, record_type, title, created_at }>}
   */
  async createIssue(issue) {
    return this._request("POST", "/issues", issue);
  }

  /**
   * List issue records for the current app.
   * @param {{ record_type?: string, status?: string, source_channel?: string, limit?: number }} opts
   * @returns {Promise<{ count, items: object[] }>}
   */
  async listIssues(opts = {}) {
    const params = new URLSearchParams();
    if (opts.record_type) params.set("record_type", opts.record_type);
    if (opts.status) params.set("status", opts.status);
    if (opts.source_channel) params.set("source_channel", opts.source_channel);
    if (opts.limit) params.set("limit", String(opts.limit));
    return this._request("GET", `/issues${params.toString() ? `?${params}` : ""}`);
  }

  /**
   * Fetch a single issue record.
   * @param {string} issueId
   * @returns {Promise<object>}
   */
  async issue(issueId) {
    return this._request("GET", `/issues/${encodeURIComponent(issueId)}`);
  }

  // ─── Content (public — no API key required) ───────────────────────────

  /**
   * Fetch a single published page by slug.
   * @param {string} slug
   * @returns {Promise<object>} Page document
   */
  async page(slug) {
    const params = new URLSearchParams({ app_tag: this.appTag });
    return this._request("GET", `/pages/${encodeURIComponent(slug)}?${params}`);
  }

  /**
   * List published pages.
   * @param {{ limit?: number, offset?: number }} opts
   * @returns {Promise<{ items: object[], count: number }>}
   */
  async pages({ limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ app_tag: this.appTag, limit: String(limit), offset: String(offset) });
    return this._request("GET", `/pages?${params}`);
  }

  /**
   * Fetch a single published post by slug.
   * @param {string} slug
   * @returns {Promise<object>} Post document
   */
  async post(slug) {
    const params = new URLSearchParams({ app_tag: this.appTag });
    return this._request("GET", `/posts/${encodeURIComponent(slug)}?${params}`);
  }

  /**
   * List published posts.
   * @param {{ limit?: number, offset?: number }} opts
   * @returns {Promise<{ items: object[], count: number }>}
   */
  async posts({ limit = 20, offset = 0 } = {}) {
    const params = new URLSearchParams({ app_tag: this.appTag, limit: String(limit), offset: String(offset) });
    return this._request("GET", `/posts?${params}`);
  }

  /**
   * Fetch active in-app announcements for this product.
   * @returns {Promise<{ items: object[], count: number }>}
   */
  async announcements() {
    const params = new URLSearchParams({ app_tag: this.appTag });
    return this._request("GET", `/announcements?${params}`);
  }

  // ─── Events ────────────────────────────────────────────────────────────

  /**
   * Push an app event (error, analytics, lifecycle).
   * @param {string} eventType - e.g. 'unhandled_error', 'page_view', 'feature_usage'
   * @param {object} data - Event payload
   * @param {string} severity - 'info' | 'warning' | 'error' | 'critical'
   * @returns {Promise<{ status, request_id }>}
   */
  async sendEvent(eventType, data = {}, severity = "info") {
    return this._request("POST", "/events", {
      event_type: eventType,
      data,
      severity,
    });
  }

  // ─── Knowledge Base ────────────────────────────────────────────────────

  /**
   * Search the knowledge base for the app.
   * @param {string} query - Search terms
   * @param {object} opts - { category?, limit? }
   * @returns {Promise<{ count, entries[] }>}
   */
  async searchKB(query, opts = {}) {
    const params = new URLSearchParams({ q: query });
    if (opts.category) params.set("category", opts.category);
    if (opts.limit) params.set("limit", String(opts.limit));
    return this._request("GET", `/kb/search?${params}`);
  }

  // ─── Feedback ──────────────────────────────────────────────────────────

  /**
   * Submit user satisfaction feedback for a widget response.
   * @param {string} sessionId
   * @param {'positive'|'negative'|'neutral'} satisfaction
   * @param {string} followup - Optional text follow-up
   * @returns {Promise<{ status }>}
   */
  async feedback(sessionId, satisfaction, followup = null, questionHash = null) {
    return this._request("POST", "/feedback", {
      session_id: sessionId,
      satisfaction,
      followup,
      question_hash: questionHash,
    });
  }
}

export { LifeSuiteClient, resolveBaseUrl };
